/**
 * useReservationCharges — TanStack Query hook over
 * `GET /organizations/{slug}/reservations/{id}/charges` (SCRUM-336).
 *
 * Mirrors `use-reservation-folio.ts` EXACTLY: the same five-branch status
 * union `loading | ready | forbidden | notFound | error`, the same
 * Symbol-sentinel posture, the same org-scoped queryKey — over the frozen
 * per-line charges read-model (SCRUM-273's two-layer `ChargesSnapshot`).
 *
 * This is the ledger's Region A: the frozen line-item source, gated by
 * `booking.reservations.read` (the SAME reservation-scoped read resource as
 * the detail — there is no `*.charges.*` slug). It self-describes its
 * collection mode via the discriminated `kind` (`computed` / PropertyCollect
 * | `channel-remittance` / ChannelCollect | `block` / NonStayBlock), so the
 * ledger renders its line-item region even when the folio (Region B) is 403.
 *
 * A 404 → `notFound` (honest-empty: a never-frozen / pre-confirm snapshot,
 * anti-enumeration) — a DISTINCT, non-error state.
 *
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per CLAUDE.md
 *     non-negotiable §1)
 *   - `queryKey = [...bookingQueryKeys.charges(id), orgSlug]`
 *   - `enabled: orgSlug !== null && id != null`
 *   - `retry: false`
 *   - the response envelope is `{ data: <charges body> }` — the `ready`
 *     result unwraps `result.data.data`
 *   - every branch carries a `refetch` function
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { ChargesBody } from './to-ledger-view.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * 403 sentinel — the caller lacks `booking.reservations.read`. Own
 * module-level Symbol, distinct from the folio hook's `FOLIO_FORBIDDEN`.
 */
export const CHARGES_FORBIDDEN = Symbol('reservation-charges:forbidden');

/**
 * 404 sentinel — never-frozen snapshot (pre-confirm; anti-enum). A distinct,
 * non-error state: the tab renders honest-empty for the line-item region.
 */
export const CHARGES_NOT_FOUND = Symbol('reservation-charges:notFound');

export type UseReservationChargesResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /**
       * The frozen charges body, unwrapped from the `{ data }` envelope —
       * discriminated by `kind` (`computed` | `channel-remittance` | `block`).
       */
      readonly data: ChargesBody;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'notFound';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly refetch: () => void;
    };

export function useReservationCharges(
  reservationId: string | null,
): UseReservationChargesResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    // `charges(id)` requires a string; when id is null the query is disabled
    // so the key is never used — an empty string keeps the tuple type stable.
    queryKey: [...bookingQueryKeys.charges(reservationId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && reservationId != null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/reservations/{id}/charges',
        {
          params: {
            path: { slug: orgSlug as string, id: reservationId as string },
          },
        },
      );
      if (result.response.status === 403) {
        return CHARGES_FORBIDDEN;
      }
      if (result.response.status === 404) {
        return CHARGES_NOT_FOUND;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations/${reservationId}/charges failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || reservationId == null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === CHARGES_FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (query.data === CHARGES_NOT_FOUND) {
    return { status: 'notFound', refetch };
  }

  // `envelope.data` is the generated three-arm `ChargesBody` union
  // (`computed | channel-remittance | block`) — no cast needed.
  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    refetch,
  };
}
